/* eslint-disable jsx-a11y/anchor-is-valid */
import {useMemo, useState} from 'react'
import {Link, useSearchParams} from 'react-router-dom'
import {toDateInputValue} from '../wallet/walletDateRange'
import {TotalUsers} from './TotalUsers'
import UserOverview from './UserOverview'
import {
  buildDashboardFilterState,
  DASHBOARD_QUICK_PRESETS,
  DashboardDatePreset,
  DashboardFilterState,
  formatDashboardRangeSummary,
  getDefaultDashboardFilter,
  toDashboardApiFilter,
} from './dashboardDateRange'

const presetButtonClass = (active: boolean) =>
  `btn btn-sm ${active ? 'btn-primary' : 'btn-light-primary'}`

const DashboardWrapper = () => {
  const [filter, setFilter] = useState<DashboardFilterState>(getDefaultDashboardFilter)
  const [monthValue, setMonthValue] = useState(
    filter.month || toDateInputValue(new Date()).slice(0, 7)
  )
  const [params, setParams] = useSearchParams()
  const showWelcome = params.get('welcome') === '1'

  const apiFilter = useMemo(() => toDashboardApiFilter(filter), [filter])
  const rangeSummary = formatDashboardRangeSummary(filter)

  const applyPreset = (preset: DashboardDatePreset) => {
    setFilter(buildDashboardFilterState(preset))
  }

  const applyMonth = (value: string) => {
    setMonthValue(value)
    setFilter(buildDashboardFilterState('month', value))
  }

  const dismissWelcome = () => {
    params.delete('welcome')
    setParams(params, {replace: true})
  }

  return (
    <div className='d-flex flex-column gap-6'>
      {showWelcome ? (
        <div className='alert alert-primary d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-0'>
          <div>
            <strong>Welcome!</strong> For better security, enable two-factor authentication in{' '}
            <Link to='/account/settings' className='alert-link'>
              Account settings
            </Link>
            .
          </div>
          <button type='button' className='btn btn-sm btn-light' onClick={dismissWelcome}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className='card overflow-hidden'>
        <div className='card-body p-8 p-xl-10'>
          <div className='d-flex flex-column flex-xl-row justify-content-between align-items-xl-start gap-6 gap-xl-8'>
            <div className='flex-grow-1 min-w-0'>
              <span className='badge badge-light-primary fw-bold mb-3'>Admin dashboard</span>
              <h1 className='fw-bold text-gray-900 mb-2'>Shrido network overview</h1>
              <div className='text-muted fs-6'>
                Monitor users, trips, gift cards, and wallet activity for{' '}
                <span className='text-gray-800 fw-semibold'>{rangeSummary}</span>.
              </div>
            </div>

            <div className='card border border-gray-200 shadow-none flex-shrink-0 w-100 w-xl-auto min-w-xl-420px'>
              <div className='card-body p-4 p-lg-5'>
                <div className='d-flex align-items-center justify-content-between gap-3 mb-3'>
                  <div className='text-gray-800 fw-bold fs-7'>Date range</div>
                  <span className='badge badge-light-info fs-8'>{rangeSummary}</span>
                </div>

                <div className='d-flex flex-wrap gap-2 mb-3'>
                  {DASHBOARD_QUICK_PRESETS.map((opt) => (
                    <button
                      key={opt.id}
                      type='button'
                      className={presetButtonClass(filter.preset === opt.id)}
                      onClick={() => applyPreset(opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div className='separator separator-dashed my-3'></div>

                <div className='d-flex flex-column flex-sm-row align-items-sm-center gap-2 gap-sm-3'>
                  <label className='text-muted fw-semibold fs-7 mb-0 text-nowrap'>Month wise</label>
                  <input
                    type='month'
                    value={monthValue}
                    onChange={(e) => applyMonth(e.target.value)}
                    className={`form-control form-control-sm w-100 w-sm-175px bg-white ${
                      filter.preset === 'month' ? 'border-primary' : ''
                    }`}
                  />
                  {filter.preset === 'month' ? (
                    <span className='badge badge-light-primary text-nowrap'>Selected</span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TotalUsers />
      <UserOverview filter={apiFilter} />
    </div>
  )
}

export {DashboardWrapper}
